import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { LangflowEmbedService } from 'services/integrations/langflow-embed.service';
import { LangflowFlowConfig } from './langflow-flows';

@Component({
  selector: 'app-langflow-chat-drawer',
  templateUrl: './langflow-chat-drawer.component.html',
  styleUrls: ['./langflow-chat-drawer.component.scss'],
})
export class LangflowChatDrawerComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() isOpen = false;
  @Input() flowConfig!: LangflowFlowConfig;
  @Input() tweaksJson = '{}';

  @Output() closed = new EventEmitter<void>();

  @ViewChild('chatContainer') chatContainerRef: ElementRef<HTMLElement>;

  scriptLoaded = false;
  isScriptLoading = false;
  chatWidth = 0;
  chatHeight = 0;

  private resizeObserver: ResizeObserver | null = null;

  constructor(private _langflowEmbedService: LangflowEmbedService) {}

  get langflowConfig() {
    return this._langflowEmbedService.config;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']?.currentValue === true && !changes['isOpen']?.previousValue) {
      this.onOpen();
      this.scheduleDimensionUpdate();
    }

    if (changes['isOpen']?.currentValue === false) {
      this.resetDimensions();
    }
  }

  ngAfterViewInit(): void {
    if (this.isOpen) {
      this.scheduleDimensionUpdate();
    }
  }

  ngOnDestroy(): void {
    this.disconnectResizeObserver();
  }

  close(): void {
    this.closed.emit();
  }

  private onOpen(): void {
    if (this._langflowEmbedService.isScriptLoaded) {
      this.scriptLoaded = true;
      this.scheduleDimensionUpdate();
      return;
    }

    this.isScriptLoading = true;
    this._langflowEmbedService
      .loadScript()
      .then(() => {
        this.scriptLoaded = true;
        this.isScriptLoading = false;
        this.scheduleDimensionUpdate();
      })
      .catch(() => {
        this.isScriptLoading = false;
        this.closed.emit();
      });
  }

  private scheduleDimensionUpdate(): void {
    setTimeout(() => {
      requestAnimationFrame(() => {
        this.updateChatDimensions();
        this.setupResizeObserver();
      });
    }, 0);
  }

  private updateChatDimensions(): void {
    const el = this.chatContainerRef?.nativeElement;
    if (!el) {
      return;
    }

    const { width, height } = el.getBoundingClientRect();
    const nextWidth = Math.floor(width);
    const nextHeight = Math.floor(height);

    if (nextWidth > 0 && nextHeight > 0) {
      this.chatWidth = nextWidth;
      this.chatHeight = nextHeight;
    }
  }

  private setupResizeObserver(): void {
    if (this.resizeObserver || !this.isOpen) {
      return;
    }

    const el = this.chatContainerRef?.nativeElement;
    if (!el) {
      return;
    }

    this.resizeObserver = new ResizeObserver(() => {
      this.updateChatDimensions();
    });
    this.resizeObserver.observe(el);
  }

  private disconnectResizeObserver(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
  }

  private resetDimensions(): void {
    this.disconnectResizeObserver();
    this.chatWidth = 0;
    this.chatHeight = 0;
  }
}
